import DeleteIcon from "@mui/icons-material/DeleteOutline";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import Box from "@mui/material/Box";
import { styled, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import { PageTab } from "_shared/teams/teams-tabs";
import _ from "lodash";
import { populateIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import {
  deleteTeamTemplate,
  getErrorLoadingTeamTemplatesByQuery,
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
  getTeamTemplatesQueryByInterface,
  populateTeamTemplatesByQuery,
  updateTeamTemplatesInterfaceQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplateForm from "work/team-templates/_views/team-template-form";
import TeamTemplate from "work/team-templates/team-template";
import {
  LowerTopToolbarContainer,
  RowTitle,
  TableTitle,
  TableTopToolbarContainer,
  TeamsTabs,
  ToolbarButtons,
  ToolbarButtonsContainer,
} from "./teams-page";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";

const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const NameCell = ({ row }: { row: Row<TeamTemplate> }) => <RowTitle>{row.original.name?.value ?? ""}</RowTitle>;

const columnHelper = createMRTColumnHelper<TeamTemplate>();

export default function Teams() {
  // Refs 
  const isDirtyRef = React.useRef<boolean>(false);
  const isCreateTeamTemplateOpen = React.useRef<boolean>(false);

  // State
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState();

  // Route
  const routeParams = useLoaderData();
  const activeTab: PageTab = routeParams.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Templates;

  // Hooks
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialog();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();

  // Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const interfaceName = "TeamsPage";
  const teamTemplatesQuery = useSelector(getTeamTemplatesQueryByInterface(interfaceName));
  const teamTemplatesResponse = useSelector(getTeamTemplatesByQuery(teamTemplatesQuery));
  const isLoadingTeamTemplates = useSelector(getIsLoadingTeamTemplatesByQuery(teamTemplatesQuery));
  const errorLoadingTeamTemplates = useSelector(getErrorLoadingTeamTemplatesByQuery(teamTemplatesQuery));

  const beginCreateTeamTemplate = React.useCallback(() => {
    if(isCreateTeamTemplateOpen.current) return;
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);
    
    isCreateTeamTemplateOpen.current = true;
    openDialog({
      title: "Create Template Team",
      MuiProps: { fullWidth: false, maxWidth: "lg" },
      contentSxProps: { display: "flex" },
      isDirtyRef: isDirtyRef,
      disableSave: true,
      component: (
        <TeamTemplateForm
          navigate={navigate}
          onFormDirty={(dirty) => {
            isDirtyRef.current = dirty === true;
          }}
          onSave={() => {
            isDirtyRef.current = false;
            navigate(parentPath.pathname);
          }}
        />
      ),
      onClose: () => {
        isDirtyRef.current = false;
        isCreateTeamTemplateOpen.current = false;
        navigate(parentPath.pathname);
      },
    });
  }, [openDialog, navigate]);

  const handleEditTeamTemplate = React.useCallback((id: Guid) => {
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

    let pathName = parentPath.pathname;
    if (pathName.endsWith(routeParams.action + "/")) {
      pathName = pathName.substring(0, pathName.length - (routeParams.action.length + 1));
    }

    openDialog({
      title: "Edit Template Team",
      MuiProps: { fullWidth: false, maxWidth: "lg" },
      contentSxProps: { display: "flex" },
      isDirtyRef: isDirtyRef,
      disableSave: true,
      component: (
        <TeamTemplateForm
          templateId={id}
          navigate={navigate}
          onFormDirty={(dirty) => {
            isDirtyRef.current = dirty === true;
          }}
          onSave={() => {
            isDirtyRef.current = false;
            closeDialog();
            navigate(pathName);
          }}
        />
      ),
      onClose: () => {
        isDirtyRef.current = false;
        closeDialog();
        navigate(pathName);
      },
    });
  }, [routeParams.action, openDialog, navigate, closeDialog]);

  useEffect(() => {
    if (sessionPermissions || sessionPermissionsError || sessionPermissionsLoading) return;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  const teamTemplateUserIds = React.useMemo(
    () =>
      teamTemplatesResponse?.data.reduce<Guid[]>((ids: Guid[], template: TeamTemplate) => {
        ids.push(template.leader.userId);
        template.members.forEach((member) => ids.push(member.userId));
        return _.uniqBy(ids, (id) => id.value);
      }, []) ?? [],

    [teamTemplatesResponse],
  );

  const defaultTeamTemplatesQuery = React.useMemo(() => {
    return new TeamTemplateQuery({
      filterParameters: TeamTemplateFilterParameters.create({
        creatorEntityId: session.user?.legalEntityId,
        name: columnFilters.find((filter) => filter.id === "name")?.value as string,
        isArchived: routeParams.pageTab === PageTab.Archived,
      }),
      orderParameters: TeamTemplateOrderParameters.create({
        orderBy: TeamTemplateOrderField.Name,
        orderDir: OrderDirection.Ascending,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 10 }),
    });
  }, [columnFilters, routeParams.pageTab, session.user?.legalEntityId]);

  useEffect(() => {
    if (teamTemplatesQuery) return;

    dispatch(
      updateTeamTemplatesInterfaceQuery({
        interfaceName,
        query: defaultTeamTemplatesQuery,
      }),
    );
  }, [defaultTeamTemplatesQuery, dispatch, teamTemplatesQuery]);

  useEffect(() => {
    if (teamTemplatesResponse?.data || !teamTemplatesQuery || isLoadingTeamTemplates || errorLoadingTeamTemplates) return;
    dispatch(
      populateTeamTemplatesByQuery({
        session,
        query: teamTemplatesQuery,
      }),
    );
  }, [dispatch, errorLoadingTeamTemplates, isLoadingTeamTemplates, session, teamTemplatesQuery, teamTemplatesResponse?.data]);

  useEffect(() => {
    if (teamTemplateUserIds.length === 0) return;

    dispatch(
      populateIndividualsByUserIds({
        session,
        userIds: teamTemplateUserIds,
      }),
    );
  }, [dispatch, session, teamTemplateUserIds]);

  const nameFilter = React.useMemo(() => columnFilters.find((filter) => filter.id === "name")?.value as string, [columnFilters]);
  const leaderFilter = React.useMemo(() => columnFilters.find((filter) => filter.id === "leader")?.value as string, [columnFilters]);
  const memberFilter = React.useMemo(() => columnFilters.find((filter) => filter.id === "members")?.value as string, [columnFilters]);

  const query = React.useMemo(() => {
    return new TeamTemplateQuery({
      filterParameters: TeamTemplateFilterParameters.create({
        creatorEntityId: session.user?.legalEntityId,
        name: nameFilter,
        leaderEntityId: leaderFilter ? new Guid(leaderFilter) : undefined,
        teamMemberEntityId: memberFilter ? new Guid(memberFilter) : undefined,
        isArchived: routeParams.pageTab === PageTab.Archived,
      }),
      orderParameters: TeamTemplateOrderParameters.create({
        orderBy: TeamTemplateOrderField.Name,
        orderDir: sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending,
      }),
      paginationParameters: PaginationParameters.create(pagination),
    });
  }, [leaderFilter, memberFilter, nameFilter, pagination, routeParams.pageTab, session.user?.legalEntityId, sorting]);

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId || !session.user?.isCompanyManager) return;

    if (!routeParams.pageTab) {
      navigate(`/teams/${PageTab.Templates}`);
    }
    if (routeParams.action === "create") {
      beginCreateTeamTemplate();
      return;
    }
    if (routeParams.pageTab === PageTab.Templates && routeParams.action === "edit" && routeParams.id) {
      handleEditTeamTemplate(new Guid(routeParams.id));
    }

    if (query.cacheKey === teamTemplatesQuery?.cacheKey) return;

    dispatch(updateTeamTemplatesInterfaceQuery({ interfaceName, query }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    beginCreateTeamTemplate, 
    dispatch, 
    handleEditTeamTemplate, 
    navigate, 
    query?.cacheKey, 
    routeParams.action, 
    routeParams.id, 
    routeParams.pageTab, 
    session.user?.isCompanyManager, 
    session.user?.legalEntityId?.value, 
    teamTemplatesQuery?.cacheKey
  ]);

  async function handleDeleteTeamTemplate(id: Guid) {
    try {
      const response = await confirm({
        title: "Delete?",
        message: `Delete team template?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      await dispatch(
        deleteTeamTemplate({
          session,
          id,
        }),
      ).unwrap();

      enqueueSnackbar("Deleted vendor-team template", { variant: "success" });

      await dispatch(
        populateTeamTemplatesByQuery({
          session,
          query: teamTemplatesQuery!,
        }),
      ).unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete vendor-team template. Please try again", {
        variant: "error",
      });
    }
  }

  const renderLeaderColumn = React.useCallback((team: TeamTemplate) => {
    return <LeaderColumnCell teamTemplate={team} teamUserIds={teamTemplateUserIds} />;
  }, [teamTemplateUserIds]);

  const renderMembersColumn = React.useCallback((template: TeamTemplate) => {
    return <MembersColumnCell teamTemplate={template} teamUserIds={teamTemplateUserIds} />;
  }, [teamTemplateUserIds]);

  const renderCompanyColumn = React.useCallback((template: TeamTemplate) => {
    return <CompanyColumnCell teamTemplate={template} teamUserIds={teamTemplateUserIds} />;
  }, [teamTemplateUserIds]);

  const columns = React.useMemo(() => [
    columnHelper.accessor((team) => team.name?.value ?? "", {
      header: "Template Name",
      id: "name",
      Cell: NameCell,
      enableColumnFilter: false,
    }),
    columnHelper.accessor(renderCompanyColumn, {
      header: "Company",
      id: "company",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor(renderLeaderColumn, {
      header: "Leader",
      id: "leader",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor(renderMembersColumn, {
      header: "Members",
      id: "members",
      enableSorting: false,
      enableColumnFilter: false,
    }),
  ], [renderCompanyColumn, renderLeaderColumn, renderMembersColumn]);

  const tableData = React.useMemo(() => teamTemplatesResponse?.data ?? [], [teamTemplatesResponse]);

  const tableOptions: MRT_TableOptions<TeamTemplate> = {
    columns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (row.original.id) {
          navigate(`/teams/templates/edit/${row.original.id?.value}`);
        }
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button startIcon={<GroupAddIcon />} onClick={() => navigate("/teams/templates/create")}>
              Create Team
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate("/teams/templates/create")}>
            <GroupAddIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Teams</TableTitle>
          <LowerTopToolbarContainer>
            <TeamsTabs activeTab={activeTab} />
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? "",
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    renderRowActions: ({ row }) => {
      if (activeTab !== PageTab.Templates) return null;
      return (
        <Tooltip title="Delete Template">
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                if (!row.original.id) return;
                void handleDeleteTeamTemplate(row.original.id);
              }}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </span>
        </Tooltip>
      );
    },
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: errorLoadingTeamTemplates
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: teamTemplatesResponse?.totalElements ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: !teamTemplatesResponse?.data.length && isLoadingTeamTemplates,
      pagination,
      showAlertBanner: Boolean(errorLoadingTeamTemplates),
      showProgressBars: isLoadingTeamTemplates,
      sorting,
      showSkeletons: !teamTemplatesResponse && isLoadingTeamTemplates,
    },
  }

  if (!sessionPermissions) return <LoaderContainer><Loader fontSize="20px" /></LoaderContainer>;
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;

  return <MRTNoMemo<TeamTemplate> options={tableOptions} />;
}
